import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { fetchBlobUrl } from "../../lib/fileOpen";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FirstAidKit, MapTrifold, Phone, ArrowsClockwise, PencilSimple, NavigationArrow, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Static route map (site → A&E) that opens Google Maps directions when clicked. */
export function AeRouteMap({ src, directionsUrl, testId = "ae-map" }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke = "";
    setUrl(""); setFailed(false);
    if (!src) return undefined;
    fetchBlobUrl(src).then(u => { revoke = u; setUrl(u); }).catch(() => setFailed(true));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [src]);
  if (failed) return null;
  const img = url
    ? <img src={url} alt="Route to nearest A&E" className="w-full rounded-md border border-border object-cover" data-testid={testId} />
    : <div className="w-full aspect-video rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center text-xs text-muted-foreground" data-testid={`${testId}-loading`}>Loading route map…</div>;
  return directionsUrl ? (
    <a href={directionsUrl} target="_blank" rel="noreferrer" className="block group relative" title="Open directions in Google Maps" data-testid={`${testId}-link`}>
      {img}
      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-background/90 border border-border px-2.5 py-1 text-[11px] font-medium shadow-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors"><NavigationArrow className="h-3.5 w-3.5" weight="fill" /> Directions</span>
    </a>
  ) : img;
}

/** RAMS editor: nearest A&E found from the site address, editable, with a clickable route map. */
export default function NearestAeBox({ ramsId, emergency, onChange, hasAddress }) {
  const ae = emergency?.nearest_ae || null;
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  const lookup = async (payload = {}) => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hs/rams/${ramsId}/ae-lookup`, payload);
      onChange(data.emergency);
      setMapKey(k => k + 1);
      toast.success(`Nearest A&E: ${data.emergency?.nearest_ae?.name || "found"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const patch = (p) => onChange({ ...emergency, nearest_ae: { ...(ae || {}), ...p, source: "manual" }, hospital: p.name ?? ae?.name ?? emergency?.hospital });
  const clear = () => { const em = { ...emergency }; delete em.nearest_ae; em.hospital = ""; onChange(em); };

  return (
    <div className="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20 p-3 space-y-2" data-testid="rams-ae-box">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300 flex items-center gap-1"><FirstAidKit className="h-3.5 w-3.5" weight="fill" /> Nearest A&amp;E / emergency department</p>
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => lookup()} disabled={busy || !hasAddress} title={hasAddress ? "Find the closest emergency department to the site" : "Add the site address first"} data-testid="rams-ae-lookup">
            <ArrowsClockwise className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> {ae ? "Re-find" : "Find nearest A&E"}
          </Button>
          {ae && <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(e => !e)} title="Edit details" data-testid="rams-ae-edit"><PencilSimple className="h-3.5 w-3.5" /></Button>}
          {ae && <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={clear} title="Clear" data-testid="rams-ae-clear"><X className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>

      {!ae && <p className="text-xs text-muted-foreground" data-testid="rams-ae-empty">{hasAddress ? "Not set — click Find nearest A&E, or enter it manually." : "Pull a job (with a site address) to find the closest A&E automatically."}{!hasAddress && <button type="button" className="ml-1 underline" onClick={() => { setEditing(true); patch({ name: "" }); }} data-testid="rams-ae-manual">Enter manually</button>}</p>}

      {ae && !editing && (
        <div className="grid md:grid-cols-[1fr_minmax(180px,45%)] gap-3 items-start">
          <div className="text-xs space-y-1" data-testid="rams-ae-details">
            <p className="text-sm font-semibold" data-testid="rams-ae-name">{ae.name}</p>
            {ae.address && <p className="text-muted-foreground" data-testid="rams-ae-address">{ae.address}</p>}
            {ae.phone && <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> <a href={`tel:${ae.phone}`} className="hover:underline" data-testid="rams-ae-phone">{ae.phone}</a></p>}
            {ae.distance_text && <p className="flex items-center gap-1 text-muted-foreground"><MapTrifold className="h-3 w-3" /> <span data-testid="rams-ae-distance">{ae.distance_text}</span>{ae.site?.address ? <span> from site</span> : null}</p>}
            {ae.directions_url && <a href={ae.directions_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="rams-ae-directions"><NavigationArrow className="h-3 w-3" weight="fill" /> Get directions in Google Maps</a>}
            {ae.source === "manual" && <p className="text-[10px] text-muted-foreground">Entered manually</p>}
            {(ae.alternatives || []).length > 0 && (
              <details className="pt-1">
                <summary className="cursor-pointer text-[11px] text-muted-foreground">Other emergency departments nearby</summary>
                <ul className="mt-1 space-y-0.5">
                  {ae.alternatives.map(a => (
                    <li key={a.place_id || a.name} className="flex items-center justify-between gap-2">
                      <span className="truncate">{a.name}<span className="text-muted-foreground"> · {a.straight_miles} mi</span></span>
                      {a.place_id && <button type="button" className="text-[11px] text-primary underline shrink-0" disabled={busy} onClick={() => lookup({ place_id: a.place_id })} data-testid={`rams-ae-alt-${a.place_id}`}>use</button>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          {ae.lat != null && ae.site?.lat != null && <AeRouteMap key={mapKey} src={`/hs/rams/${ramsId}/ae-map.png?v=${mapKey}`} directionsUrl={ae.directions_url} testId="rams-ae-map" />}
        </div>
      )}

      {ae && editing && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="rams-ae-editor">
          <div className="sm:col-span-2"><Label className="text-[11px]">Hospital / department name</Label><Input value={ae.name || ""} onChange={e => patch({ name: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-ae-input-name" /></div>
          <div className="sm:col-span-2"><Label className="text-[11px]">Address</Label><Input value={ae.address || ""} onChange={e => patch({ address: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-ae-input-address" /></div>
          <div><Label className="text-[11px]">Phone</Label><Input value={ae.phone || ""} onChange={e => patch({ phone: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-ae-input-phone" /></div>
          <div><Label className="text-[11px]">Distance / time</Label><Input value={ae.distance_text || ""} onChange={e => patch({ distance_text: e.target.value })} placeholder="e.g. 2.1 miles · 9 min" className="h-8 text-xs mt-1" data-testid="rams-ae-input-distance" /></div>
          <div className="sm:col-span-2 flex justify-end"><Button type="button" size="sm" className="h-7 text-[11px]" onClick={() => setEditing(false)} data-testid="rams-ae-done">Done</Button></div>
        </div>
      )}
    </div>
  );
}
